<template>
  <div>
    <transition name="asuo" appear>
      <main id="content" region="main content">
        <!-- section-explore-atlas -->
        <section-explore-atlas
          v-if="$page && exploreItems"
          :title="$page.core404.title ? $page!.core404.title : ''"
          title-level="h1"
          title-size="small"
          event-section="Explore information"
          :items="exploreItems"
          @select-item="handleSelectItemClick($event, `${$page!.core404.title}`)"
          @link-click="handleLinkClick($event, `${$page!.core404.title}`)"
          @update-item="handleUpdateItemClick($event, `${$page!.core404.title}`)"
        >
          <template #body>
            <p class="fs-large fw-bold mb-space-md">
              We can’t find the requested URL “{{ currentRoute.fullPath }}”
            </p>
            <div
              v-if="$page && $page.core404.description"
              class="mb-space-lg"
              v-html="$page.core404.description"
            ></div>
          </template>
        </section-explore-atlas>
        <!-- fallback when the 404 content can't be loaded (PDASUOW-26507) -->
        <section v-else class="container py-space-md py-lg-space-xxl">
          <h1 class="mb-space-md">Oops! Page not found</h1>
          <p class="fs-large fw-bold mb-space-md">
            We can’t find the requested URL “{{ currentRoute.fullPath }}”
          </p>
          <!-- a plain link so the homepage loads in full: the app failed to start -->
          <a href="/" class="btn btn-secondary fw-bold">Go to the ASU Online homepage</a>
        </section>
      </main>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import type { NuxtError } from "#app";
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { SectionExploreAtlas } from "@rds-vue-ui/section-explore-atlas";
import type { ExploreItem } from "@rds-vue-ui/section-explore-atlas/SectionExploreAtlas.vue";
import type { Core404 } from "~/server/resolvers/resolvers-types";
const UtilityService = useUtilityService();
const config = useRuntimeConfig();
const router = useRouter();
const { currentRoute } = router;

const props = defineProps({
  error: Object as () => NuxtError
});

interface Core404PageData {
  core404: Core404;
}

const $page = await usePageQuery<Core404PageData>(
  `#graphql
      query {
        core404(id:"adad7655-140a-40e4-af0c-554df87661a2") {
          id
          detailPage
          metaTitle
          metaDescription
          title
          description
          exploreItems {
            exploreItems {
              image
              imageAlt
              title
              listItems {
                uri
                text
                target
              }
            }
          }
        }
      }`,
  {},
  {
    key: "404-page",
    // This page can render on any route, e.g. after the app fails to start on
    // `/`, but its data is only prerendered into /404/. See PDASUOW-26507.
    payloadPath: "/404/"
  }
);

// the fallback title keeps the tab titled when the 404 content can't load
const metaTitle = $page?.core404.metaTitle ?? "Page Not Found 404 | ASU online";

useHead({
  title: metaTitle,
  meta: [
    { name: "Title", content: metaTitle },
    {
      name: "Description",
      content: $page?.core404.metaDescription
    }
  ],
  link: [
    {
      rel: "canonical",
      href: UtilityService.formatCanonicalUrl(config.public.siteUrl)
    }
  ]
});

const exploreItems = computed(() => {
  return $page && $page?.core404.exploreItems!.exploreItems
    ? ($page.core404.exploreItems?.exploreItems as ExploreItem[])
    : undefined;
});

const handleSelectItemClick = (event: Event, title: string) => {
  const selectTextTarget = event.target as HTMLElement;
  analyticsComposable.trackSelectEvent(
    "onclick",
    "open",
    "dropdown",
    "main content",
    title.toLowerCase(),
    `explore information about ${selectTextTarget.innerText.toLowerCase()}`
  );
};
const handleLinkClick = (event: Event, section: string) => {
  event.preventDefault();
  const eventTarget = event.target as HTMLLinkElement;

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    section.toLowerCase(),
    eventTarget.innerText.toLowerCase()
  );
  const linkHref = eventTarget.href;
  if (!linkHref) console.error("link missing href");
  const linkTarget = eventTarget.target ? eventTarget.target : "_blank";
  if (linkHref) {
    navigateTo(linkHref, {
      open: {
        target: linkTarget
      }
    });
  }
};
const handleUpdateItemClick = (event: Event, section: string) => {
  if (event.target instanceof HTMLElement) {
    analyticsComposable.trackSelectEvent(
      "onclick",
      "click",
      "dropdown",
      "main content",
      section.toLowerCase(),
      event.target.innerText.toLowerCase()
    );
  } else {
    console.error("dropdown missing text value");
  }
};

onMounted(() => {
  console.error(props.error);
});
</script>

<style lang="scss" scoped>
.asuo-enter-active {
  transition: opacity 0.5s;
}
.asuo-enter {
  opacity: 0;
}
</style>
